"use client"

import { useState, useEffect } from "react"
import { format } from "date-fns"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { useToast } from "@/components/ui/use-toast"
import { Patient, Doctor, Appointment, AppointmentFormData } from "@/types"
import { supabase } from "@/utils/supabase/client"
import { Loader2 } from "lucide-react"
import { Combobox } from "@/components/ui/combobox"

interface AppointmentFormModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (appointment: Partial<AppointmentFormData>) => void
  appointmentToEdit: Appointment | null
  patients: Patient[]
  doctors: Doctor[]
}

export function AppointmentFormModal({
  isOpen,
  onClose,
  onSave,
  appointmentToEdit,
  patients,
  doctors,
}: AppointmentFormModalProps) {
  const { toast } = useToast()
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null)
  const [selectedDoctor, setSelectedDoctor] = useState<Doctor | null>(null)
  const [appointmentDate, setAppointmentDate] = useState("")
  const [appointmentTime, setAppointmentTime] = useState("")
  const [appointmentType, setAppointmentType] = useState("")
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (appointmentToEdit) {
      setSelectedPatient(appointmentToEdit.patients)
      setSelectedDoctor(appointmentToEdit.doctors)
      setAppointmentDate(format(new Date(appointmentToEdit.appointment_date), "yyyy-MM-dd"))
      setAppointmentTime(appointmentToEdit.appointment_time || "")
      setAppointmentType(appointmentToEdit.appointment_type)
    } else {
      setSelectedPatient(null)
      setSelectedDoctor(null)
      setAppointmentDate("")
      setAppointmentTime("")
      setAppointmentType("")
    }
  }, [appointmentToEdit])

  const handleSubmit = () => {
    if (!selectedPatient || !selectedDoctor || !appointmentDate) {
      toast({ title: "All fields are required", variant: "destructive" })
      return
    }
    setIsLoading(true)
    onSave({
      patient: selectedPatient,
      doctor: selectedDoctor,
      appointment_date: new Date(appointmentDate).toISOString(),
      appointment_time: appointmentTime,
      appointment_type: appointmentType,
      status: appointmentToEdit?.status || "Scheduled",
    })
    setIsLoading(false)
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {appointmentToEdit ? "Edit Appointment" : "Create Appointment"}
          </DialogTitle>
          <DialogDescription>
            {appointmentToEdit ? "Update the details of the appointment." : "Fill in the details to create a new appointment."}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="patient">Patient</Label>
            <Combobox
              items={patients.map(p => ({ value: p.id, label: p.name }))}
              value={selectedPatient?.id || ''}
              onChange={(value) => setSelectedPatient(patients.find(p => p.id === value) || null)}
              placeholder="Select patient"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="doctor">Doctor</Label>
            <Combobox
              items={doctors.map(d => ({ value: d.id, label: d.name }))}
              value={selectedDoctor?.id || ''}
              onChange={(value) => setSelectedDoctor(doctors.find(d => d.id === value) || null)}
              placeholder="Select doctor"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
                <Label htmlFor="appointmentDate">Appointment Date</Label>
                <Input
                id="appointmentDate"
                type="date"
                value={appointmentDate}
                onChange={(e) => setAppointmentDate(e.target.value)}
                />
            </div>
            <div className="grid gap-2">
                <Label htmlFor="appointmentTime">Appointment Time</Label>
                <Input
                id="appointmentTime"
                type="time"
                value={appointmentTime}
                onChange={(e) => setAppointmentTime(e.target.value)}
                />
            </div>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="appointmentType">Appointment Type</Label>
            <Input
              id="appointmentType"
              value={appointmentType}
              onChange={(e) => setAppointmentType(e.target.value)}
              placeholder="e.g., Follow-up, Consultation"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isLoading}>
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Appointment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
